import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button, Tabs, Typography, Space, Row, Col, Form, Input, Breadcrumb, Result, Spin, notification } from "antd";
import { MailOutlined, Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";
import permissionMetaData from "../../../data/permission";
import { isSuper } from "../../../utils/permission";

//
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import Name from "../../../components/General/Name";
import UserPermissionSelector from "../../../components/MerchantUser/UserPermissionSelector";
import UserStatus from "../../../components/Table/UserStatus";

//
import useAuthStore from "../../../states/useAuth";
import useShopStore from "../../../states/useShop";
import useShop from "../../../hooks/useShop";
import useRequestStore from "../../../states/useRequest";
import useUserStore from "../../../states/useUser";
import useUser from "../../../hooks/useUser";
import PermissionPopover from "../../../components/PermissionPopover";

const { Title, Text, Paragraph } = Typography;

const Create = () => {
  const { id } = useParams(); // Get the :id parameter from the URL
  const history = useHistory();
  const location = useLocation();

  const { auth } = useAuthStore();
  const { resetUser } = useUserStore();
  const { newUser: newUserFromStore, updateNewUser } = useRequestStore();
  const { getSingleUser, createUser, updateUser } = useUser();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Form
  const [userForm] = Form.useForm();
  const [newUser, setNewUser] = useState(newUserFromStore);

  //
  const [originalUser, setOriginalUser] = useState();
  const [user, setUser] = useState();
  const [isFetchingSingleUser, setIsFetchingSingleUser] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const isActionBtnDisabled =
    id == "new" ? !c.hasDiff(originalUser, newUser) : !originalUser || !c.hasDiff(originalUser, user);

  const submitUserForm = async () => {
    //check form
    try {
      await userForm.validateFields();
    } catch (error) {
      log.error(error);
      return c.message({
        type: "error",
        content: "Some of the input fields are invalid. Please review and save again",
      });
    }

    let before = id == "new" ? newUser : user;

    if (_.isEmpty(c.getObjectDiff(permissionMetaData.default.no, before?.permission))) {
      return c.message({ type: "error", content: "please select permission" });
    }

    // Get the diff between originalUser and request
    let diff = c.getObjectDiff(originalUser, before);
    console.log("Changed fields:", diff);

    //if permission has diff, overwrite permission
    diff.permission = before.permission;

    if (!diff) {
      return c.message({ type: "error", content: "no information changed" });
    }

    // axios
    try {
      setIsLoadingSubmit(true);

      let response;
      if (id === "new") {
        response = await createUser(diff);
      } else {
        response = await updateUser(before.id, diff);
      }

      // Update data
      setOriginalUser(response);
      setUser(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      //redirect
      if (id === "new") {
        history.replace(`/merchantUser/user/${response.id}`);
      }
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const updateUserData = (newData) => {
    log.debug(newData);

    const update = (prev) => {
      // Create a copy of the previous shop data
      let updated = _.merge({}, prev, newData);

      if (newData.permission) {
        updated.permission = newData.permission;
      }

      log.debug(updated);
      return updated;
    };

    if (id == "new") {
      setNewUser(update);
    } else {
      setUser(update);
    }
  };

  const getSingleUserData = async () => {
    //setActiveTab
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    // const queryParams = c.parseQueryToJSON(queryURL);
    // const { tab } = queryParams;
    // log.debug(tab);
    // setActiveTab(tab);

    //
    log.debug(id);
    if (id == "new") {
      return;
    }

    let result;

    try {
      setIsFetchingSingleUser(true);
      result = await getSingleUser(id);
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    }

    setIsFetchingSingleUser(false);
    log.debug(result);

    setOriginalUser(result);
    setUser(result);
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetUser();
      updateNewUser("whole", undefined); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getSingleUserData();
  }, [id]);

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingSingleUser}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/user");
                    }}
                  >
                    User List
                  </a>
                ),
              },
              {
                title: <a>{id == "new" ? "Create User" : "User Profile"}</a>,
              },
            ]}
          />

          <PageHeader
            title={id == "new" ? "Create User" : "User Profile"}
            isSticky
            actionGroup={
              <Space>
                <Button
                  size="large"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  color="default"
                  variant="filled"
                  style={{ marginRight: "1.5rem", width: "7.5rem" }}
                  onClick={async () => {
                    //reset
                    if (id == "new") {
                      setNewUser(originalUser);
                    } else {
                      setUser(originalUser);
                    }
                  }}
                >
                  Reset
                </Button>

                <Button
                  size="large"
                  type="primary"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  style={{ width: "7.5rem" }}
                  onClick={() => {
                    submitUserForm();
                  }}
                >
                  {id == "new" ? "Create User" : "Update"}
                </Button>
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <SectionHeader title={"Basic Info"} />
          <Form form={userForm} style={{ width: "100%" }}>
            {/* Basic Info */}
            <Row align="middle" style={{ marginBottom: "2rem" }}>
              <Col span={2} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                Email
              </Col>
              <Col>
                {id == "new" ? (
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        type: "email",
                        message: "Invalid email format",
                      },
                      { required: true, message: "required" },
                    ]}
                  >
                    <Input
                      value={newUser?.email}
                      size="large"
                      addonBefore={<MailOutlined />}
                      placeholder="user@example.com"
                      style={{ width: "20rem" }}
                      onChange={(e) => {
                        updateUserData({ email: e.target.value });
                      }}
                    />
                  </Form.Item>
                ) : (
                  <>{user?.email}</>
                )}
              </Col>
            </Row>
            <Row align="middle" style={{ marginBottom: "2rem" }}>
              <Col span={2} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                Username
              </Col>
              <Col>
                <Name
                  state={{
                    data: id == "new" ? newUser?.name : user?.name,
                    form: userForm,
                    rules: [{ required: true, message: "required" }],
                    onChange: updateUserData,
                  }}
                  readOnly={false}
                  placeholder={"Username"}
                  style={{ width: "20rem" }}
                />
              </Col>
            </Row>
            {id == "new" || isSuper(user?.permission) ? null : (
              <Row align="middle" style={{ marginBottom: "2rem" }}>
                <Col span={2} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                  Password
                </Col>
                <Col>
                  {user?.newUserSetting?.hasResetFirstPw ? (
                    "User already reset password"
                  ) : (
                    <Paragraph copyable style={{ margin: 0 }}>
                      {user?.newUserSetting?.firstPassword}
                    </Paragraph>
                  )}
                </Col>
                <Col span={24}>
                  <Text type="secondary">This is a temporary password, user can reset password after login</Text>
                </Col>
              </Row>
            )}

            <Row align="middle" style={{ marginBottom: "2rem" }}>
              <Col span={2} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                Status
              </Col>
              <Col>
                <UserStatus status={user?.isDisable} />
              </Col>
              <Col style={{ marginLeft: "2rem" }}>
                <Button
                  size="large"
                  style={{ marginRight: "8px" }}
                  onClick={() => {
                    updateUserData({ isDisable: !user?.isDisable });
                  }}
                >
                  {user?.isDisable ? "Activate User" : "Disable User"}
                </Button>
              </Col>
            </Row>

            {/* Permission */}
            <SectionHeader
              title={"Permission"}
              description={"Manage permissions easily using our role-based permission system!"}
            />

            {isSuper(user?.permission) ? (
              <Row>
                <Row style={{ width: "100%", fontWeight: "bold", marginBottom: "1rem" }}>
                  You are Super Admin, includes all permission for all shops:
                </Row>
                <Space>
                  <PermissionPopover id="report" />
                  <PermissionPopover id="shop" />
                  <PermissionPopover id="product" />
                  <PermissionPopover id="eStamp" />
                  <PermissionPopover id="takeaway" />
                  <PermissionPopover id="promotionCode" />
                  <PermissionPopover id="welcome" />
                  <PermissionPopover id="ad" />
                  <PermissionPopover id="pushNotification" />
                  <PermissionPopover id="redeem" />
                  <PermissionPopover id="eStampQR" />
                  <PermissionPopover id="takeawayQR" />
                </Space>
              </Row>
            ) : (
              <UserPermissionSelector
                permission={id == "new" ? newUser?.permission : user?.permission}
                readOnly={false}
                onChange={updateUserData}
              />
            )}
          </Form>
        </>
      )}
    </div>
  );
};

export default Create;
